import React, { useState, useEffect, useCallback } from 'react';
import { FiMail, FiRefreshCw, FiArchive, FiCornerUpLeft, FiPlus, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import {
  sincronizarEmails, listarEmailsPainel, listarEmailsDaConta,
  buscarCorpoEmail, arquivarEmail, excluirEmail, enviarNovoEmail, responderEmail,
} from '../../services/email';
import { listarContasEmail } from '../../services/emailContas';
import styles from './Email.module.css';
import useEsc from '../../hooks/useEsc';

/* Painel de e-mail unificado -- lê/responde/arquiva via IMAP real (ver
   backend/src/services/email-imap.js e email-smtp.js). Contas ficam
   cadastradas em Configurações > E-mail. "Painel" junta todas as contas
   por ordem de chegada; "Gerenciamento" trabalha uma conta de cada vez
   (enviar novo, responder, arquivar). */

const ABAS = [
  { id: 'painel', label: 'Painel' },
  { id: 'gerenciamento', label: 'Gerenciamento' },
];

function formatarData(iso) {
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  return mesmoDia
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function Email() {
  const [aba, setAba] = useState('painel');
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcone}><FiMail size={18} /></div>
        <div>
          <h1 className={styles.titulo}>E-mail</h1>
          <p className={styles.subtitulo}>Todas as contas num painel só — leitura, resposta e arquivamento via IMAP.</p>
        </div>
      </div>

      <div className={styles.tabBar}>
        {ABAS.map(a => (
          <button
            key={a.id} type="button"
            className={`${styles.tab} ${aba === a.id ? styles.tabAtiva : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className={styles.conteudo}>
        {aba === 'painel' ? <PainelTab /> : <GerenciamentoTab />}
      </div>
    </div>
  );
}

/* ══════════════════════════ Painel (unificado) ══════════════════════════ */

function PainelTab() {
  const [emails, setEmails] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [erro, setErro] = useState('');
  const [abertoId, setAbertoId] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    listarEmailsPainel().then(r => setEmails(r.items || [])).catch(e => setErro(e.message)).finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  async function sincronizar() {
    setSincronizando(true); setErro('');
    try {
      const r = await sincronizarEmails();
      const falhas = (r.items || []).filter(x => !x.ok);
      if (falhas.length) setErro(`Falha ao sincronizar: ${falhas.map(f => `${f.nome} (${f.error})`).join('; ')}`);
      carregar();
    } catch (e) { setErro(e.message); }
    finally { setSincronizando(false); }
  }

  /* sincronização automática a cada 10min -- silenciosa (não passa pelo
     "Carregando..." nem mexe no botão), pra não interromper quem está
     lendo. O botão continua ali pra quando precisar na hora. Pula a
     rodada se já tiver uma sincronização manual em andamento. */
  useEffect(() => {
    const t = setInterval(async () => {
      if (sincronizando) return;
      try {
        await sincronizarEmails();
        const r = await listarEmailsPainel();
        setEmails(r.items || []);
      } catch (e) {
        setErro(e.message);
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [sincronizando]);

  function aoArquivar(id) { setEmails(prev => prev.filter(e => e.id !== id)); if (abertoId === id) setAbertoId(null); }
  function aoResponder(id) { setEmails(prev => prev.map(e => e.id === id ? { ...e, respondido: true } : e)); }
  function aoAbrir(email) { setAbertoId(email.id); if (!email.lido) setEmails(prev => prev.map(e => e.id === email.id ? { ...e, lido: true } : e)); }

  const emailAberto = emails.find(e => e.id === abertoId) || null;

  return (
    <div className={styles.painelWrap}>
      <div className={styles.listaColuna}>
        <div className={styles.listaTopo}>
          <span className={styles.contagem}>{emails.length} e-mail{emails.length !== 1 ? 's' : ''}</span>
          <button type="button" className={styles.btnSecundario} onClick={sincronizar} disabled={sincronizando}>
            <FiRefreshCw size={13} className={sincronizando ? styles.girando : ''} /> {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
          </button>
        </div>
        {erro && <p className={styles.erro}>{erro}</p>}
        {carregando ? <p className={styles.vazio}>Carregando…</p> : (
          <ListaEmails emails={emails} abertoId={abertoId} onAbrir={aoAbrir} mostrarConta />
        )}
      </div>
      <div className={styles.leituraColuna}>
        {emailAberto
          ? <LeitorEmail email={emailAberto} onArquivar={aoArquivar} onExcluir={aoArquivar} onResponder={aoResponder} />
          : <div className={styles.leituraVazia}>Selecione um e-mail pra ler</div>}
      </div>
    </div>
  );
}

/* ══════════════════════════ Gerenciamento (por conta) ══════════════════════════ */

function GerenciamentoTab() {
  const [contas, setContas] = useState([]);
  const [contaId, setContaId] = useState(null);
  const [pasta, setPasta] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [abertoId, setAbertoId] = useState(null);
  const [composeAberto, setComposeAberto] = useState(false);

  useEffect(() => {
    listarContasEmail()
      .then(r => {
        const ativas = (r.items || []).filter(c => c.ativo);
        setContas(ativas);
        if (ativas.length) setContaId(ativas[0].id);
      })
      .catch(e => setErro(e.message));
  }, []);

  const carregar = useCallback(() => {
    if (!contaId) return;
    setCarregando(true); setAbertoId(null);
    listarEmailsDaConta(contaId, pasta).then(r => setEmails(r.items || [])).catch(e => setErro(e.message)).finally(() => setCarregando(false));
  }, [contaId, pasta]);
  useEffect(() => { carregar(); }, [carregar]);

  function aoArquivar(id) { setEmails(prev => prev.filter(e => e.id !== id)); if (abertoId === id) setAbertoId(null); }
  function aoResponder(id) { setEmails(prev => prev.map(e => e.id === id ? { ...e, respondido: true } : e)); }
  function aoAbrir(email) { setAbertoId(email.id); if (!email.lido) setEmails(prev => prev.map(e => e.id === email.id ? { ...e, lido: true } : e)); }

  const emailAberto = emails.find(e => e.id === abertoId) || null;
  const conta = contas.find(c => c.id === contaId) || null;

  if (!contas.length) {
    return <p className={styles.vazio}>Nenhuma conta de e-mail ativa. Cadastre em Configurações › E-mail.</p>;
  }

  return (
    <div>
      <div className={styles.gerTopo}>
        <select className={styles.select} value={contaId || ''} onChange={e => setContaId(e.target.value)}>
          {contas.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.email}</option>)}
        </select>
        <div className={styles.pastaChips}>
          <button type="button" className={`${styles.chip} ${pasta === 'inbox' ? styles.chipOn : ''}`} onClick={() => setPasta('inbox')}>Recebidos</button>
          <button type="button" className={`${styles.chip} ${pasta === 'arquivadas' ? styles.chipOn : ''}`} onClick={() => setPasta('arquivadas')}>Arquivados</button>
        </div>
        <div className={styles.spacer} />
        <button type="button" className={styles.btnPrimario} onClick={() => setComposeAberto(true)}><FiPlus size={13} /> Novo e-mail</button>
      </div>

      {erro && <p className={styles.erro}>{erro}</p>}

      <div className={styles.painelWrap}>
        <div className={styles.listaColuna}>
          {carregando ? <p className={styles.vazio}>Carregando…</p> : <ListaEmails emails={emails} abertoId={abertoId} onAbrir={aoAbrir} />}
        </div>
        <div className={styles.leituraColuna}>
          {emailAberto
            ? <LeitorEmail email={emailAberto} onArquivar={aoArquivar} onExcluir={aoArquivar} onResponder={aoResponder} />
            : <div className={styles.leituraVazia}>Selecione um e-mail pra ler</div>}
        </div>
      </div>

      {composeAberto && (
        <ComposeModal conta={conta} onFechar={() => setComposeAberto(false)} onEnviado={() => { setComposeAberto(false); carregar(); }} />
      )}
    </div>
  );
}

/* ══════════════════════════ peças compartilhadas ══════════════════════════ */

function ListaEmails({ emails, abertoId, onAbrir, mostrarConta }) {
  if (!emails.length) return <p className={styles.vazio}>Nenhum e-mail.</p>;
  return (
    <ul className={styles.lista}>
      {emails.map(e => (
        <li
          key={e.id}
          className={`${styles.item} ${e.id === abertoId ? styles.itemAtivo : ''} ${!e.lido ? styles.itemNaoLido : ''}`}
          onClick={() => onAbrir(e)}
        >
          <div className={styles.itemTopo}>
            <span className={styles.itemDe}>{e.de_nome || e.de_email || '(remetente desconhecido)'}</span>
            <span className={styles.itemData}>{formatarData(e.recebido_em)}</span>
          </div>
          <div className={styles.itemAssunto}>{e.assunto || '(sem assunto)'}</div>
          {e.resumo && <div className={styles.itemResumo}>{e.resumo}</div>}
          <div className={styles.itemRodape}>
            {mostrarConta && e.conta_nome && <span className={styles.badgeConta}>{e.conta_nome}</span>}
            {e.respondido && <span className={styles.badgeRespondido}><FiCornerUpLeft size={10} /> respondido</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function LeitorEmail({ email, onArquivar, onExcluir, onResponder }) {
  const [corpo, setCorpo] = useState(null);
  const [carregandoCorpo, setCarregandoCorpo] = useState(true);
  const [erro, setErro] = useState('');
  const [respondendo, setRespondendo] = useState(false);
  const [textoResposta, setTextoResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [arquivando, setArquivando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  useEffect(() => {
    setCorpo(null); setErro(''); setCarregandoCorpo(true); setRespondendo(false); setTextoResposta('');
    buscarCorpoEmail(email.id).then(setCorpo).catch(e => setErro(e.message)).finally(() => setCarregandoCorpo(false));
  }, [email.id]);

  async function arquivar() {
    setArquivando(true);
    try { await arquivarEmail(email.id); onArquivar(email.id); }
    catch (e) { setErro(e.message); }
    finally { setArquivando(false); }
  }

  async function excluir() {
    if (!window.confirm('Excluir este e-mail? Não tem como desfazer.')) return;
    setExcluindo(true);
    try { await excluirEmail(email.id); onExcluir(email.id); }
    catch (e) { setErro(e.message); }
    finally { setExcluindo(false); }
  }

  async function enviarResposta() {
    if (!textoResposta.trim()) return;
    setEnviando(true); setErro('');
    try {
      await responderEmail(email.id, { texto: textoResposta.trim() });
      onResponder(email.id);
      setRespondendo(false); setTextoResposta('');
    } catch (e) { setErro(e.message); }
    finally { setEnviando(false); }
  }

  return (
    <div className={styles.leitor}>
      <div className={styles.leitorTopo}>
        <div>
          <h3 className={styles.leitorAssunto}>{email.assunto || '(sem assunto)'}</h3>
          <p className={styles.leitorDe}>
            {email.de_nome ? `${email.de_nome} <${email.de_email}>` : email.de_email} — {new Date(email.recebido_em).toLocaleString('pt-BR')}
          </p>
        </div>
        <div className={styles.leitorAcoes}>
          <button type="button" className={styles.btnSecundario} onClick={() => setRespondendo(v => !v)}>
            <FiCornerUpLeft size={13} /> Responder
          </button>
          <button type="button" className={styles.btnSecundario} onClick={arquivar} disabled={arquivando}>
            <FiArchive size={13} /> {arquivando ? 'Arquivando…' : 'Arquivar'}
          </button>
          <button type="button" className={`${styles.btnSecundario} ${styles.btnPerigo}`} onClick={excluir} disabled={excluindo}>
            <FiTrash2 size={13} /> {excluindo ? 'Excluindo…' : 'Excluir'}
          </button>
        </div>
      </div>

      {erro && <p className={styles.erro}>{erro}</p>}

      {carregandoCorpo ? <p className={styles.vazio}>Carregando corpo…</p> : corpo?.html ? (
        <iframe title="corpo do e-mail" sandbox="allow-same-origin" srcDoc={corpo.html} className={styles.corpoIframe} />
      ) : (
        <pre className={styles.corpoTexto}>{corpo?.texto || '(sem conteúdo)'}</pre>
      )}

      {corpo?.anexos?.length > 0 && (
        <div className={styles.anexos}><FiPaperclip size={12} /> {corpo.anexos.map(a => a.nome).join(', ')}</div>
      )}

      {respondendo && (
        <div className={styles.respostaBox}>
          <textarea rows={4} value={textoResposta} placeholder="Escreva sua resposta…" onChange={e => setTextoResposta(e.target.value)} />
          <div className={styles.mfootInline}>
            <button type="button" className={styles.btnPrimario} onClick={enviarResposta} disabled={enviando || !textoResposta.trim()}>
              {enviando ? 'Enviando…' : 'Enviar resposta'}
            </button>
            <button type="button" className={styles.btnSecundario} onClick={() => setRespondendo(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ComposeModal({ conta, onFechar, onEnviado }) {
  useEsc(onFechar);
  const [para, setPara] = useState('');
  const [assunto, setAssunto] = useState('');
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  async function enviar() {
    if (!para.trim() || !assunto.trim()) { setErro('Preencha destinatário e assunto.'); return; }
    setEnviando(true); setErro('');
    try {
      await enviarNovoEmail(conta.id, { para: para.trim(), assunto: assunto.trim(), texto });
      onEnviado();
    } catch (e) { setErro(e.message); }
    finally { setEnviando(false); }
  }

  return (
    <div className={styles.ov}>
      <div className={styles.modal}>
        <h3>Novo e-mail — {conta.nome}</h3>
        <div className={styles.fld}><label>Para</label><input value={para} onChange={e => setPara(e.target.value)} placeholder="destinatario@exemplo.com" /></div>
        <div className={styles.fld}><label>Assunto</label><input value={assunto} onChange={e => setAssunto(e.target.value)} /></div>
        <div className={styles.fld}><label>Mensagem</label><textarea rows={8} value={texto} onChange={e => setTexto(e.target.value)} /></div>
        {erro && <p className={styles.erro}>{erro}</p>}
        <div className={styles.mfootInline}>
          <button type="button" className={styles.btnPrimario} onClick={enviar} disabled={enviando}>{enviando ? 'Enviando…' : 'Enviar'}</button>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

export default Email;
